/**
 * useOrgRequestActions hook
 *
 * Mutations for a single org request: delete (soft-cancel) and the §4(3)
 * "Beauftragen" (planned → opass_binding_confirmed). Each is sent against the
 * revision the member sees when confirming, with a retained operation: after
 * an unconfirmed answer the same action repeats on the revision it first went
 * out on (useUnconfirmedRevision), even when the page has read a newer one
 * since (useOrgRequestActions.test.tsx). Delete navigates to the list on
 * success; confirmBinding does not navigate (the caller decides via its
 * refresh). Each exposes its error for the caller to show inline
 * (`deleteError`, `confirmError`).
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAggregateCommand, useUnconfirmedRevision } from "@/hooks/commands";
import { routes } from "@/lib/routes";
type NoInput = Record<string, never>;
export function useOrgRequestActions(requestId) {
  const $ = _c(20);
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      method: "DELETE"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const cancel = useAggregateCommand(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      method: "POST"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const confirm = useAggregateCommand(t1);
  const cancelRetries = useUnconfirmedRevision();
  const confirmRetries = useUnconfirmedRevision();
  const [deleteError, setDeleteError] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  let t2;
  if ($[2] !== cancel || $[3] !== cancelRetries || $[4] !== requestId || $[5] !== router) {
    t2 = async shown => {
      setDeleteError(null);
      const url = `/api/tenant/requests/${requestId}`;
      const revision = cancelRetries.revisionFor(url, shown);
      const outcome = await cancel.send({
        url,
        input: {},
        revision
      });
      cancelRetries.answered(url, revision, outcome.kind === "unavailable");
      if (outcome.kind === "committed") {
        router.push(routes.organization.requests.list);
        return true;
      }
      setDeleteError(outcome.message);
      return false;
    };
    $[2] = cancel;
    $[3] = cancelRetries;
    $[4] = requestId;
    $[5] = router;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const deleteRequest = t2;
  let t3;
  if ($[7] !== confirm || $[8] !== confirmRetries || $[9] !== requestId) {
    t3 = async shown_0 => {
      setConfirmError(null);
      const url_0 = `/api/tenant/requests/${requestId}/confirm-binding`;
      const revision_0 = confirmRetries.revisionFor(url_0, shown_0);
      const outcome_0 = await confirm.send({
        url: url_0,
        input: {},
        revision: revision_0
      });
      confirmRetries.answered(url_0, revision_0, outcome_0.kind === "unavailable");
      if (outcome_0.kind === "committed") {
        return true;
      }
      setConfirmError(outcome_0.message);
      return false;
    };
    $[7] = confirm;
    $[8] = confirmRetries;
    $[9] = requestId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const confirmBinding = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setDeleteError(null);
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setConfirmError(null);
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== cancel.pending || $[14] !== confirm.pending || $[15] !== confirmBinding || $[16] !== confirmError || $[17] !== deleteError || $[18] !== deleteRequest) {
    t6 = {
      deleteRequest,
      deleting: cancel.pending,
      deleteError,
      resetDeleteError: t4,
      confirmBinding,
      confirming: confirm.pending,
      confirmError,
      resetConfirmError: t5
    };
    $[13] = cancel.pending;
    $[14] = confirm.pending;
    $[15] = confirmBinding;
    $[16] = confirmError;
    $[17] = deleteError;
    $[18] = deleteRequest;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}